import React, { FC } from 'react';

import { SCREEN_XS, SCREEN_SM_MAX, SCREEN_MD_MAX } from '../../lib/ui/breakpoints';

import { ImageDetails, ImgType, getSrcSet, getSourceElements } from './imgUtils';

import {
  FeaturedImageWrapper,
  ForegroundImagesWrapper,
  StyledBackgroundImage,
  StyledForegroundImage,
  StyledForegroundPicture,
  StyledPicture,
} from './FeaturedImage.styles';

export enum BackgroundType {
  COLOR = 'color',
  IMAGE = 'image',
}

export type BackgroundInfo =
  | {
      type: BackgroundType.COLOR;
      parameters: IBackgroundColor;
    }
  | {
      type: BackgroundType.IMAGE;
      parameters: IBackgroundImage;
    };

interface IBackgroundColor {
  color: string;
}

interface IBackgroundImage {
  alt: string;
  /** What image should be passed as the default src, typically the tablet size */
  featuredSrc: string;
  jpg?: ImageDetails[];
  webp?: ImageDetails[];
  png?: ImageDetails[];
  /** Defaults to loading: lazy */
  loading?: 'lazy' | 'eager';
}

export interface FeaturedImageProps {
  background: BackgroundInfo;
  /** The foregroundImage is used if there are "floating" assets on top of the background */
  foregroundImage?: {
    mobileSrc: string;
    tabletSrc: string;
    desktopSrc: string;
    imgType: ImgType;
    alt?: string;
    /** Defaults to loading: lazy */
    loading?: 'lazy' | 'eager';
  };
  foreground?: IBackgroundImage;
}

/**
 * An image that is utilized in either the Masthead or Differentiation components
 */
const FeaturedImage: FC<FeaturedImageProps> = ({ background, foregroundImage, foreground }) => {
  const foregroundImageDetails: ImageDetails[] = foregroundImage
    ? [
        { url: foregroundImage.mobileSrc, breakpoint: SCREEN_XS.toString() },
        { url: foregroundImage.tabletSrc, breakpoint: SCREEN_SM_MAX.toString() },
        { url: foregroundImage.desktopSrc, breakpoint: SCREEN_MD_MAX.toString() },
      ]
    : [];

  const renderForeground = () => {
    if (foreground) {
      return (
        <>
          {getSourceElements(foreground.webp, 'webp')}
          {getSourceElements(foreground.png, 'png')}
          <StyledForegroundImage
            src={foreground.featuredSrc}
            srcSet={getSrcSet(foreground.png)}
            alt={foreground.alt || ''}
            loading={foreground.loading || 'lazy'}
          />
        </>
      );
    }
    if (foregroundImage) {
      return (
        <>
          {getSourceElements(foregroundImageDetails, foregroundImage.imgType)}
          <StyledForegroundImage
            src={foregroundImage.tabletSrc}
            srcSet={getSrcSet(foregroundImageDetails)}
            alt={foregroundImage.alt || ''}
            loading={foregroundImage.loading || 'lazy'}
          />
        </>
      );
    }
    return null;
  };

  return (
    <FeaturedImageWrapper
      backgroundColor={
        background.type === BackgroundType.COLOR ? background.parameters.color : undefined
      }
    >
      {background.type === BackgroundType.IMAGE && (
        <StyledPicture>
          {getSourceElements(background.parameters.webp, 'webp')}
          {getSourceElements(background.parameters.jpg, 'jpg')}
          <StyledBackgroundImage
            src={background.parameters.featuredSrc}
            srcSet={getSrcSet(background.parameters.jpg)}
            alt={background.parameters.alt}
            loading={background.parameters.loading || 'lazy'}
          />
        </StyledPicture>
      )}
      {(foregroundImage || foreground) && (
        <ForegroundImagesWrapper isWithBackgroundImage={background.type === BackgroundType.IMAGE}>
          <StyledForegroundPicture>{renderForeground()}</StyledForegroundPicture>
        </ForegroundImagesWrapper>
      )}
    </FeaturedImageWrapper>
  );
};

export default FeaturedImage;
